
import { defineComponent } from 'vue'
import { faTimesCircle, faSave, faDownload, faTrash, faPaperclip, faFileAlt } from '@fortawesome/free-solid-svg-icons'
import { PrimaryButton, Button, Select, TextArea } from '@design-system/ui'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import SessionService from '../SessionService'
import { format, parseISO, addMinutes } from 'date-fns'
import Toast from '../components/Toast.vue'
import CustomInput from '../components/input/Input.vue'
import api from '@/services/api'

export default defineComponent({
  name: 'ComissaoEdit',
  components: {
    PrimaryButton,
    Select,
    CustomInput,
    TextArea,
    Button,
    Toast,
    FontAwesomeIcon
  },
  data () {
    return {
      loading: false,
      toastSuccess: true,
      message: '',
      hidden: true,
      softwareHouseCnpj: '',
      timeSeen: 20000,
      onboardingLink: '',
      downloadInProgress: false,
      buttonIcons: {
        cancel: {
          icon: faTimesCircle
        },
        save: {
          icon: faSave,
          inconCustomClass: 'filter brightness-0 invert flex items-end'
        },
        download: {
          icon: faDownload
        },
        trash: {
          icon: faTrash
        },
        upload: {
          icon: faPaperclip
        },
        file: {
          icon: faFileAlt
        }
      },
      isApprovalUser: false,
      commissionRequestData: {
        protocol: '',
        status: '',
        originalStatus: '',
        interval: '',
        value: 0,
        message: '',
        createdAt: '',
        updatedAt: ''
      },
      commissionRequestForm: {
        receiptDate: '',
        message: '',
        file: null
      },
      error: false,
      errorMessage: '',
      maxFileSize: 3,
      types: "['image/png', 'image/jpg', 'image/jpeg', 'application/pdf']",
      commissionRequestStatus: {
        pending: 'Aguardando',
        rejected: 'Rejeitado',
        scheduled: 'Agendado',
        finished: 'Finalizado'
      },
      approveOrRejectCommissionRequestModal: false,
      commissionRequestStatusAccepted: [
        {
          value: 'Aprovado',
          key: 'approved'
        },
        {
          value: 'Rejeitado',
          key: 'rejected'
        }
      ],
      selectedCommissionRequestStatusToUpdate: {
        value: 'Selecione...',
        key: ''
      }
    }
  },
  mounted () {
    const session = new SessionService()
    this.isApprovalUser = session.approvalAccess()
    this.$store.commit('changeActiveMenu', this.isApprovalUser ? 5 : 4)
    this.softwareHouseCnpj = this.$route.params.softwareHouseCnpj.toString()
    this.getCommissionData(this.$route.params.protocol.toString())
  },
  methods: {
    async getCommissionData (protocol) {
      this.loading = true

      try {
        const token = localStorage.getItem('token')
        const response = await api.get(`/commission-request/${this.softwareHouseCnpj}/protocol/${protocol}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        const data = response.data
        this.commissionRequestData = data
        this.commissionRequestData.originalStatus = data.status

        if (data.status === 'approved') {
          const currentDate = new Date()
          const currentDateAdjusted = addMinutes(currentDate, currentDate.getTimezoneOffset())
          const receiptDate = new Date(this.commissionRequestData.receiptDate)
          const receiptDateAdjusted = addMinutes(receiptDate, receiptDate.getTimezoneOffset())

          if (receiptDateAdjusted < currentDateAdjusted) {
            this.commissionRequestData.status = 'finished'
          } else {
            this.commissionRequestData.status = 'scheduled'
            this.commissionRequestData.message = `Previsão de pagamento: ${format(receiptDateAdjusted, 'dd/MM/yyyy')}`
          }
        }
        const startDateISO = this.parseISODate(this.commissionRequestData.startDate)
        const endDateISO = this.parseISODate(this.commissionRequestData.endDate)

        const startDateUTC = new Date(startDateISO.getTime() + (startDateISO.getTimezoneOffset() * 60000))
        const endDateUTC = new Date(endDateISO.getTime() + (endDateISO.getTimezoneOffset() * 60000))
        this.commissionRequestData.interval = `${format(startDateUTC, 'dd/MM/yyyy')} - ${format(endDateUTC, 'dd/MM/yyyy')}`
        this.loading = false
      } catch (error) {
        this.loading = false
        this.hidden = false
        this.toastSuccess = false
        this.message = error.response && error.response.data && error.response.data.error_messages[0] ? error.response.data.error_messages[0] : 'Ocorreu um erro ao buscar os dados da solicitação de comissão'
        setTimeout(() => {
          this.hidden = true
        }, this.timeSeen)
      }
    },
    async updateCommissionRequest () {
      this.loading = true

      try {
        const token = localStorage.getItem('token')
        const commissionRequestForm = this.commissionRequestForm

        const data = new FormData()
        data.append('status', commissionRequestForm.status)
        data.append('receiptDate', format(commissionRequestForm.receiptDate, 'yyyy-MM-dd'))
        data.append('message', commissionRequestForm.message)
        data.append('files', commissionRequestForm.file)

        const response = await api.patch(`/commission-request/${this.commissionRequestData.protocol}`, data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        this.commissionRequestData = response.data
        this.commissionRequestData.originalStatus = response.data.status
        if (response.data.status === 'approved') {
          const currentDate = new Date()
          const currentDateAdjusted = addMinutes(currentDate, currentDate.getTimezoneOffset())
          const receiptDate = new Date(this.commissionRequestData.receiptDate)
          const receiptDateAdjusted = addMinutes(receiptDate, receiptDate.getTimezoneOffset())

          if (receiptDateAdjusted < currentDateAdjusted) {
            this.commissionRequestData.status = 'finished'
          } else {
            this.commissionRequestData.status = 'scheduled'
            this.commissionRequestData.message = `Previsão de pagamento: ${format(receiptDateAdjusted, 'dd/MM/yyyy')}`
          }
        }
        const startDateISO = this.parseISODate(this.commissionRequestData.startDate)
        const endDateISO = this.parseISODate(this.commissionRequestData.endDate)

        const startDateUTC = new Date(startDateISO.getTime() + (startDateISO.getTimezoneOffset() * 60000))
        const endDateUTC = new Date(endDateISO.getTime() + (endDateISO.getTimezoneOffset() * 60000))
        this.commissionRequestData.interval = `${format(startDateUTC, 'dd/MM/yyyy')} - ${format(endDateUTC, 'dd/MM/yyyy')}`

        this.loading = false
        this.approveOrRejectCommissionRequestModal = false
        this.hidden = false
        this.toastSuccess = true
        this.message = 'Solicitação de repasse de comissão atualizada com sucesso'
        setTimeout(() => {
          this.hidden = true
        }, this.timeSeen)
      } catch (error) {
        this.loading = false
        this.hidden = false
        this.toastSuccess = false
        this.message = error.response && error.response.data && error.response.data.error_messages[0] ? error.response.data.error_messages[0] : 'Ocorreu um erro ao atualizar a solicitação de comissão'
        setTimeout(() => {
          this.hidden = true
        }, this.timeSeen)
      }
    },
    downloadCommissionRequestAttachmentFiles () {
      this.downloadInProgress = true
      const token = localStorage.getItem('token')
      api.get(`/commission-request/files/${this.softwareHouseCnpj}/${this.commissionRequestData.protocol}/attachments`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      })
        .then(response => {
          this.downloadInProgress = false
          const url = window.URL.createObjectURL(new Blob([response.data]))

          const link = document.createElement('a')
          link.href = url

          link.setAttribute('download', `${this.softwareHouseCnpj}-${this.commissionRequestData.protocol}.zip`)

          document.body.appendChild(link)
          link.click()

          document.body.removeChild(link)
        })
        .catch(() => {
          this.downloadInProgress = false
          this.hidden = false
          this.toastSuccess = false
          this.message = 'Erro ao baixar os arquivos'
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
        })
    },
    downloadCommissionRequestPaymentProofFile () {
      this.downloadInProgress = true
      const token = localStorage.getItem('token')
      api.get(`/commission-request/files/${this.softwareHouseCnpj}/${this.commissionRequestData.protocol}/payment-proof`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/octet-stream'
        },
        responseType: 'blob'
      })
        .then(response => {
          this.downloadInProgress = false
          const fileName = response.headers['x-filename']
          const url = window.URL.createObjectURL(new Blob([response.data]))

          const link = document.createElement('a')
          link.href = url

          link.setAttribute('download', fileName)

          document.body.appendChild(link)
          link.click()

          document.body.removeChild(link)
        })
        .catch(() => {
          this.downloadInProgress = false
          this.hidden = false
          this.toastSuccess = false
          this.message = 'Erro ao baixar o comprovante de pagamento'
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
        })
    },

    resetFile () {
      this.$refs.imageUploader.value = ''
    },

    fileChangeHandler (e: Event) {
      this.errorMessage = ''
      this.error = false

      const target = e.target as HTMLInputElement

      if (this.commissionRequestForm.file) {
        this.error = true
        this.errorMessage = 'No máximo 1 arquivo pode ser enviado'
        return
      }

      Array.from(target.files).map((file) => {
        if (file.size > this.maxFileSize * 1024 * 1024) {
          this.error = true
          this.errorMessage = `O arquivo é maior que ${this.maxFileSize} MB`
        }
      })

      const files = Array.from(target.files)

      const fileCommission = files[0]

      if (!this.types.includes(fileCommission.type)) {
        this.error = true
        this.errorMessage = 'O arquivo enviado é inválido.'
      }
      if (!this.error) {
        this.commissionRequestForm.file = fileCommission
      }
      // this.showRemovedBtn = true
    },
    removeFile () {
      this.errorMessage = ''
      this.error = false
      this.commissionRequestForm.file = null
    },
    editCommissionRequest () {
      this.approveOrRejectCommissionRequestModal = true
    },
    changeField (index, value) {
      const fieldName = index.target?.name || index
      this.commissionRequestForm[fieldName] = value
    },
    handleCommissionRequestStatus (selected) {
      this.selectedCommissionRequestStatusToUpdate = selected
      this.commissionRequestForm.status = selected.key
    },
    hide () {
      this.hidden = true
    },
    formatCnpj (cnpj: string): string {
      const cnpjFormatado = `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
      return cnpjFormatado
    },
    formatDate: format,
    parseISODate: parseISO
  }
})
